import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useToastContext } from '../App';

function Backups() {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState('');
  const [backups, setBackups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const addToast = useToastContext();

  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  useEffect(() => {
    loadServers();
  }, []);

  useEffect(() => {
    if (selectedServer) {
      loadBackups();
      loadSchedule();
    }
  }, [selectedServer]);

  async function loadServers() {
    try {
      const data = await api.getServers();
      setServers(data);
      if (data.length > 0 && !selectedServer) {
        setSelectedServer(data[0].id);
      }
    } catch (e) {
      console.error(e);
    }
  }

  async function loadBackups() {
    try {
      const data = await api.getBackups(selectedServer);
      setBackups(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function loadSchedule() {
    setScheduleLoading(true);
    try {
      const data = await api.getBackupSchedule(selectedServer);
      setSchedule(data);
    } catch (e) {
      console.error(e);
      setSchedule(null);
    } finally {
      setScheduleLoading(false);
    }
  }

  async function handleScheduleSave() {
    if (!schedule) return;
    setScheduleSaving(true);
    try {
      const updated = await api.updateBackupSchedule(selectedServer, schedule);
      setSchedule(updated);
      addToast(updated.enabled ? 'Auto-Backup aktiviert' : 'Auto-Backup deaktiviert', 'success');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setScheduleSaving(false);
    }
  }

  async function handleCreate() {
    setCreating(true);
    try {
      const result = await api.createBackup(selectedServer);
      addToast(`Backup erstellt: ${result.fileName}`, 'success');
      loadBackups();
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete(fileName) {
    if (!window.confirm(`Backup "${fileName}" loeschen?`)) return;
    try {
      await api.deleteBackup(selectedServer, fileName);
      addToast('Backup geloescht', 'success');
      loadBackups();
    } catch (e) {
      addToast(e.message, 'error');
    }
  }

  async function handleRestore(fileName) {
    if (!window.confirm(`Backup "${fileName}" wiederherstellen? Der Server muss gestoppt sein und aktuelle Daten werden ueberschrieben!`)) return;
    setRestoring(fileName);
    try {
      await api.restoreBackup(selectedServer, fileName);
      addToast('Backup wiederhergestellt!', 'success');
    } catch (e) {
      addToast(e.message, 'error');
    } finally {
      setRestoring(null);
    }
  }

  function handleDownload(fileName) {
    const url = api.getBackupDownloadUrl(selectedServer, fileName);
    window.open(url, '_blank');
  }

  const currentServer = servers.find(s => s.id === selectedServer);

  return (
    <div>
      <div className="page-header">
        <h2>Backup-Verwaltung</h2>
      </div>

      <div className="flex gap-3 items-center mb-4">
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <select
            className="form-select"
            value={selectedServer}
            onChange={e => setSelectedServer(e.target.value)}
          >
            <option value="">-- Server waehlen --</option>
            {servers.map(s => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.status})
              </option>
            ))}
          </select>
        </div>
        {selectedServer && (
          <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
            {creating ? (
              <>
                <span className="spinner" /> Erstelle Backup...
              </>
            ) : (
              '+ Backup erstellen'
            )}
          </button>
        )}
      </div>

      {/* Auto-Backup Schedule Section */}
      {selectedServer && schedule && !scheduleLoading && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <h3>&#9200; Automatische Backups</h3>
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Enable toggle */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={schedule.enabled}
                  onChange={e => setSchedule({ ...schedule, enabled: e.target.checked })}
                  style={{ width: 18, height: 18 }}
                />
                <span>Auto-Backup aktivieren</span>
              </label>
            </div>

            {/* Interval */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">Intervall</label>
              <select
                className="form-select"
                value={schedule.intervalMinutes}
                onChange={e => setSchedule({ ...schedule, intervalMinutes: Number(e.target.value) })}
                disabled={!schedule.enabled}
                style={{ width: 160 }}
              >
                <option value={30}>Alle 30 Min.</option>
                <option value={60}>Jede Stunde</option>
                <option value={120}>Alle 2 Std.</option>
                <option value={180}>Alle 3 Std.</option>
                <option value={360}>Alle 6 Std.</option>
                <option value={720}>Alle 12 Std.</option>
                <option value={1440}>Taeglich</option>
              </select>
            </div>

            {/* Retention Hours */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">Alle behalten (Std.)</label>
              <input
                type="number"
                className="form-input"
                value={schedule.retentionHours}
                onChange={e => setSchedule({ ...schedule, retentionHours: Number(e.target.value) })}
                disabled={!schedule.enabled}
                min={1}
                max={168}
                style={{ width: 80 }}
              />
            </div>

            {/* Retention Days */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">1/Tag behalten (Tage)</label>
              <input
                type="number"
                className="form-input"
                value={schedule.retentionDays}
                onChange={e => setSchedule({ ...schedule, retentionDays: Number(e.target.value) })}
                disabled={!schedule.enabled}
                min={1}
                max={90}
                style={{ width: 80 }}
              />
            </div>

            {/* Retention Weeks */}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">1/Woche behalten (Wochen)</label>
              <input
                type="number"
                className="form-input"
                value={schedule.retentionWeeks}
                onChange={e => setSchedule({ ...schedule, retentionWeeks: Number(e.target.value) })}
                disabled={!schedule.enabled}
                min={1}
                max={52}
                style={{ width: 80 }}
              />
            </div>
          </div>

          {/* Quiet Hours Row */}
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 16 }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">Ruhezeit von (Uhr)</label>
              <select
                className="form-select"
                value={schedule.quietHoursStart ?? 0}
                onChange={e => setSchedule({ ...schedule, quietHoursStart: Number(e.target.value) })}
                disabled={!schedule.enabled}
                style={{ width: 100 }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="text-sm">Ruhezeit bis (Uhr)</label>
              <select
                className="form-select"
                value={schedule.quietHoursEnd ?? 8}
                onChange={e => setSchedule({ ...schedule, quietHoursEnd: Number(e.target.value) })}
                disabled={!schedule.enabled}
                style={{ width: 100 }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <p className="text-sm text-muted" style={{ marginBottom: 4 }}>
              Keine Auto-Backups waehrend der Ruhezeit
            </p>
          </div>

          {/* Summary text */}
          {schedule.enabled && (
            <p className="text-sm text-muted" style={{ marginTop: 12 }}>
              Backup alle {schedule.intervalMinutes} Min. | Ruhezeit: {String(schedule.quietHoursStart ?? 0).padStart(2, '0')}:00-{String(schedule.quietHoursEnd ?? 8).padStart(2, '0')}:00 | Alle behalten: {schedule.retentionHours}h | 1/Tag: {schedule.retentionDays} Tage | 1/Woche: {schedule.retentionWeeks} Wochen
            </p>
          )}

          {/* Last backup time */}
          {schedule.lastBackupTime && (
            <p className="text-sm text-muted" style={{ marginTop: 4 }}>
              Letztes Auto-Backup: {new Date(schedule.lastBackupTime).toLocaleString('de-DE')}
            </p>
          )}

          <div style={{ marginTop: 16 }}>
            <button className="btn btn-primary btn-sm" onClick={handleScheduleSave} disabled={scheduleSaving}>
              {scheduleSaving ? <span className="spinner" /> : 'Zeitplan speichern'}
            </button>
          </div>
        </div>
      )}

      {selectedServer && (
        <div className="card">
          <div className="card-header">
            <h3>Backups fuer {currentServer?.name || 'Server'}</h3>
            <span className="text-sm text-muted">{backups.length} Backup(s)</span>
          </div>

          {backups.length === 0 ? (
            <div className="empty-state">
              <div className="icon">&#128190;</div>
              <p>Noch keine Backups erstellt</p>
              <button className="btn btn-primary" onClick={handleCreate} disabled={creating}>
                Erstes Backup erstellen
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Dateiname</th>
                    <th>Groesse</th>
                    <th>Erstellt am</th>
                    <th>Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map(backup => (
                    <tr key={backup.fileName}>
                      <td>{backup.fileName}</td>
                      <td>{formatSize(backup.size)}</td>
                      <td>{new Date(backup.createdAt).toLocaleString('de-DE')}</td>
                      <td>
                        <div className="flex gap-2">
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => handleDownload(backup.fileName)}
                          >
                            Download
                          </button>
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleRestore(backup.fileName)}
                            disabled={restoring === backup.fileName || currentServer?.status !== 'stopped'}
                            title={currentServer?.status !== 'stopped' ? 'Server muss gestoppt sein' : ''}
                          >
                            {restoring === backup.fileName ? <span className="spinner" /> : 'Wiederherstellen'}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(backup.fileName)}
                          >
                            Loeschen
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export default Backups;
